import { ActionPayloadValidationError, requireStringField } from "@slot/client-engine";
import type { ReplayContext, RoundAction, RoundActionHandler } from "@slot/client-engine";

/** The action's name on the wire: the merge mechanic's one action (decision 47). */
export const MERGE_SYMBOLS_ACTION_TYPE = "merge_symbols";

/** One merge output: the target keeps its cell and becomes the result symbol; the consumed cells leave the grid into it. */
export interface SymbolMergeOutput {
  /** The canonical linear grid index of the cell the output lands on. */
  targetPositionIndex: number;
  /** The symbol the target becomes (one step up the title's ladder; the server's word, never the client's). */
  resultSymbolId: string;
  /** The cells that merge into the target and leave the grid: at least one. */
  consumedPositionIndices: readonly number[];
}

/** `merge_symbols`, validated: every output of one substep's clusters, which merge together. */
export interface MergeSymbolsInput {
  merges: readonly SymbolMergeOutput[];
}

/** What the handler drives (the merge mechanic's `MergePresenter`). */
export interface MergeSymbolsTarget {
  mergeSymbols(input: MergeSymbolsInput): Promise<void>;
}

/**
 * MECHANIC action `merge_symbols { merges: [{ targetPositionIndex, resultSymbolIdentifier, consumedPositionIndices }] }`
 * (decision 47): the substep's clusters merge. Each entry is one output — its target cell keeps its place and becomes
 * `resultSymbolIdentifier`, its consumed cells leave the grid; the cells of a cluster in no entry are leftovers and stay
 * as they are. No `remove_symbols_by_mask` follows: the next collapse event drops the survivors and the refill into
 * the consumed cells. One action per substep; it carries no money (the cluster's `grid_win_highlight` does).
 *
 * SERVER-AUTHORITATIVE: the client merges exactly the cells it is handed and never evaluates a cluster, the pair rule
 * or the ladder. The backend and the fixtures author it on phase "win" and never skippable: it changes the grid, so a
 * skipped merge would leave the collapse nothing to drop into (the type → phase resolver does not know game-defined
 * types, and an unknown type resolves to "other"). The handler checks the payload's shape — indices are digits, a cell
 * appears once in the whole action, an output consumes at least one cell; the presenter checks it against the grid,
 * before anything plays.
 */
export class MergeSymbolsActionHandler implements RoundActionHandler {
  public readonly actionTypeName = MERGE_SYMBOLS_ACTION_TYPE;

  public constructor(private readonly target: MergeSymbolsTarget) {}

  public async handleAction(roundAction: RoundAction, _context: ReplayContext): Promise<void> {
    const raw = roundAction.actionPayload["merges"];
    if (!Array.isArray(raw) || raw.length === 0) throw this.invalid("merges", "Must be a non-empty array.");
    const seen = new Set<number>();
    const claim = (field: string, index: number): void => {
      if (seen.has(index)) throw this.invalid(field, `Cell ${index} appears more than once in the action.`);
      seen.add(index);
    };
    const merges = raw.map((entry: unknown, i): SymbolMergeOutput => {
      if (!entry || typeof entry !== "object") throw this.invalid(`merges[${i}]`, "Must be an object.");
      const e = entry as Record<string, unknown>;
      const path = `${this.actionTypeName}.merges[${i}]`;
      const targetPositionIndex = this.positionIndex(`merges[${i}].targetPositionIndex`, requireStringField(path, e, "targetPositionIndex"));
      claim(`merges[${i}].targetPositionIndex`, targetPositionIndex);
      const resultSymbolId = requireStringField(path, e, "resultSymbolIdentifier");
      const consumed = e["consumedPositionIndices"];
      if (!Array.isArray(consumed) || consumed.length === 0) throw this.invalid(`merges[${i}].consumedPositionIndices`, "Must be a non-empty array.");
      const consumedPositionIndices = consumed.map((value: unknown, j) => {
        const field = `merges[${i}].consumedPositionIndices[${j}]`;
        if (typeof value !== "string") throw this.invalid(field, "Must be a string.");
        const index = this.positionIndex(field, value);
        claim(field, index);
        return index;
      });
      return { targetPositionIndex, resultSymbolId, consumedPositionIndices };
    });
    await this.target.mergeSymbols({ merges });
  }

  private positionIndex(field: string, value: string): number {
    if (!/^\d+$/.test(value)) throw this.invalid(field, `Must be a linear grid index (digits), got ${JSON.stringify(value)}.`);
    return Number(value);
  }

  private invalid(field: string, message: string): ActionPayloadValidationError {
    return new ActionPayloadValidationError(this.actionTypeName, field, message);
  }
}
